import { useState } from 'react';
import { Link, useParams } from 'react-router-dom';
import { toast } from 'sonner';
import {
  ArrowLeft,
  Building2,
  ChevronRight,
  ClipboardList,
  Coins,
  FileText,
  GraduationCap,
  Plus,
  Trash2,
  User,
  Wallet,
} from 'lucide-react';
import {
  ActionButton,
  ContextHero,
  DataCard,
  DateBadge,
  MoneyAmount,
  PageContainer,
  SkeletonShimmer,
  StatusBadge,
  type UniversalStatus,
} from '@/components/design-system';
import { BenefitSimulatorCard } from '@/components/design-system/BenefitSimulatorCard';
import {
  useAddDossierPaiement,
  useDeleteDossierCharge,
  useDossier,
  useDossierCharges,
  useDossierPaiements,
  useDossierSimulateur,
  useUpsertDossierCharge,
  type DossierChargeCategorie,
  type DossierPaiementType,
  type DossierStatutGlobal,
} from '@/hooks/queries/useDossiers';
import { useFormateurs, useFormation } from '@/hooks/queries/useFormation';
import { useTiers } from '@/hooks/queries/useTiers';
import { useEntreprises } from '@/hooks/queries/useEntreprises';
import { cn } from '@/lib/utils';

const STATUT_LABEL: Record<DossierStatutGlobal, string> = {
  brouillon: 'Brouillon',
  en_cours: 'En cours',
  realise: 'Réalisé',
  facture: 'Facturé',
  encaisse: 'Encaissé',
  solde: 'Soldé',
  annule: 'Annulé',
};

const STATUT_TONE: Record<DossierStatutGlobal, UniversalStatus> = {
  brouillon: 'brouillon',
  en_cours: 'en_cours',
  realise: 'planifie',
  facture: 'en_attente',
  encaisse: 'planifie',
  solde: 'paye',
  annule: 'annule',
};

const CATEGORIES: { value: DossierChargeCategorie; label: string }[] = [
  { value: 'honoraires_formateur', label: 'Honoraires formateur' },
  { value: 'hotel_lieu', label: 'Hôtel / Lieu' },
  { value: 'traiteur', label: 'Traiteur' },
  { value: 'transport', label: 'Transport' },
  { value: 'supports_impressions', label: 'Supports / Impressions' },
  { value: 'commission_commercial', label: 'Commission commercial' },
  { value: 'marketing_digital', label: 'Marketing digital' },
  { value: 'autre', label: 'Autre' },
];

type Tab = 'overview' | 'finances' | 'documents' | 'taches';

export function DossierDetailPage(): JSX.Element {
  const { id } = useParams<{ id: string }>();
  const [tab, setTab] = useState<Tab>('overview');
  const { data: dossier, isLoading } = useDossier(id ?? null);
  const { data: paiements = [] } = useDossierPaiements(id ?? null);
  const { data: sim } = useDossierSimulateur(id ?? null);
  const { data: formation } = useFormation(dossier?.formation_id ?? null);
  const { data: formateurs = [] } = useFormateurs();
  const formateur = formateurs.find((f) => f.id === dossier?.formateur_id);
  const { data: entreprises = [] } = useEntreprises();
  const eurl = entreprises.find((e) => e.nom.toLowerCase().includes('kost'));
  const { data: tiers = [] } = useTiers({ entrepriseId: eurl?.id ?? null, search: '' });
  const client = tiers.find((t) => t.id === dossier?.client_tier_id);

  if (isLoading) {
    return (
      <PageContainer>
        <SkeletonShimmer variant="hero" />
      </PageContainer>
    );
  }

  if (!dossier) {
    return (
      <PageContainer>
        <DataCard>
          Dossier introuvable.{' '}
          <Link to="/dossiers" className="text-violet-600 underline">
            Retour à la liste
          </Link>
        </DataCard>
      </PageContainer>
    );
  }

  return (
    <PageContainer>
      <div className="space-y-6">
        <Link
          to="/dossiers"
          className="inline-flex items-center gap-1.5 text-sm font-medium text-slate-600 hover:text-slate-900"
        >
          <ArrowLeft className="h-4 w-4" /> Retour aux dossiers
        </Link>

        <ContextHero
          variant="formation_b2b"
          icon={<GraduationCap className="h-5 w-5" />}
          title={dossier.titre}
          subtitle={
            (
              <span className="font-mono text-xs text-slate-500">{dossier.reference}</span>
            ) as unknown as string
          }
          actions={
            <div className="flex items-center gap-2">
              <StatusBadge
                status={STATUT_TONE[dossier.statut_global]}
                label={STATUT_LABEL[dossier.statut_global]}
                variant="large"
                withDot
              />
            </div>
          }
        />

        <nav className="flex flex-wrap items-center gap-1 border-b border-slate-200">
          {(
            [
              ['overview', "Vue d'ensemble", ClipboardList],
              ['finances', 'Finances & Paiements', Wallet],
              ['documents', 'Documents', FileText],
              ['taches', 'Tâches & Jalons', ChevronRight],
            ] as [Tab, string, React.ComponentType<{ className?: string }>][]
          ).map(([k, l, Icon]) => (
            <button
              key={k}
              onClick={() => setTab(k)}
              className={cn(
                'inline-flex items-center gap-1.5 border-b-2 px-3 py-2 text-sm font-semibold transition-colors',
                tab === k
                  ? 'border-violet-600 text-violet-700'
                  : 'border-transparent text-slate-500 hover:text-slate-700',
              )}
            >
              <Icon className="h-3.5 w-3.5" />
              {l}
            </button>
          ))}
        </nav>

        {tab === 'overview' && (
          <div className="grid gap-5 lg:grid-cols-2">
            <div className="space-y-4">
              <DataCard title="Client" trailing={<Building2 className="h-4 w-4 text-slate-400" />}>
                {client ? (
                  <>
                    <div className="text-base font-bold text-slate-900">
                      {client.raison_sociale}
                    </div>
                    {client.email && <div className="text-xs text-slate-500">{client.email}</div>}
                  </>
                ) : (
                  <div className="text-sm text-slate-400">Aucun client renseigné</div>
                )}
              </DataCard>

              <DataCard
                title="Formation"
                trailing={<GraduationCap className="h-4 w-4 text-slate-400" />}
              >
                {formation ? (
                  <>
                    <div className="text-base font-bold text-slate-900">{formation.titre}</div>
                    <div className="text-xs text-slate-500">
                      {formation.code} · {formation.duree_jours}j · {formation.niveau}
                    </div>
                  </>
                ) : (
                  <div className="text-sm text-slate-400">Aucune formation liée</div>
                )}
                <div className="mt-3 grid grid-cols-2 gap-3 text-xs">
                  <Info
                    label="Date début"
                    value={<DateBadge date={dossier.date_debut} format="long" />}
                  />
                  <Info
                    label="Date fin"
                    value={<DateBadge date={dossier.date_fin} format="long" />}
                  />
                </div>
              </DataCard>

              <DataCard title="Formateur" trailing={<User className="h-4 w-4 text-slate-400" />}>
                {formateur ? (
                  <>
                    <div className="text-base font-bold text-slate-900">
                      {formateur.prenom} {formateur.nom}
                    </div>
                    {formateur.email && (
                      <div className="text-xs text-slate-500">{formateur.email}</div>
                    )}
                    <div className="mt-2">
                      <StatusBadge status="brouillon" label={formateur.statut} />
                    </div>
                  </>
                ) : (
                  <div className="text-sm text-slate-400">Aucun formateur renseigné</div>
                )}
              </DataCard>
            </div>

            <div className="space-y-4">
              <BenefitSimulatorCard
                prixVenteHT={Number(dossier.prix_vente_ht)}
                totalCharges={Number(sim?.total_charges ?? 0)}
                charges={Object.entries(sim?.charges_par_categorie ?? {}).map(([k, v]) => ({
                  categorie: k,
                  total: Number(v),
                }))}
              />

              <DataCard
                title="Statut paiements"
                trailing={<Coins className="h-4 w-4 text-slate-400" />}
              >
                <div className="space-y-2 text-xs">
                  <Row
                    label="Facturation"
                    value={
                      <StatusBadge
                        status={dossier.statut_facturation === 'envoyee' ? 'paye' : 'en_cours'}
                        label={dossier.statut_facturation}
                      />
                    }
                  />
                  <Row
                    label="Encaissement"
                    value={
                      <StatusBadge
                        status={
                          dossier.statut_encaissement === 'solde'
                            ? 'paye'
                            : dossier.statut_encaissement === 'partiel' ||
                                dossier.statut_encaissement === 'acompte_recu'
                              ? 'en_cours'
                              : 'a_payer'
                        }
                        label={dossier.statut_encaissement}
                      />
                    }
                  />
                  <Row
                    label="Acompte reçu"
                    value={<DateBadge date={dossier.date_acompte_recu} />}
                  />
                  <Row label="Solde reçu" value={<DateBadge date={dossier.date_solde_recu} />} />
                </div>
              </DataCard>
            </div>
          </div>
        )}

        {tab === 'finances' && (
          <FinancesTab dossierId={dossier.id} prixVenteHT={Number(dossier.prix_vente_ht)} />
        )}

        {tab === 'documents' && (
          <DataCard title="Documents">
            <div className="text-sm text-slate-500">
              Module documents Sprint 6.1 — l&apos;upload Storage est prêt côté DB (bucket
              `dossiers`). Aucun document à afficher pour ce dossier.
            </div>
          </DataCard>
        )}

        {tab === 'taches' && (
          <DataCard title="Tâches & Jalons">
            <div className="text-sm text-slate-500">Module tâches/checklist Sprint 6.1.</div>
          </DataCard>
        )}

        {/* Hidden hint to silence unused vars in JSX usage check */}
        <span className="hidden">{paiements.length}</span>
      </div>
    </PageContainer>
  );
}

// =====================================================================
// FinancesTab
// =====================================================================
function FinancesTab({
  dossierId,
  prixVenteHT,
}: {
  dossierId: string;
  prixVenteHT: number;
}): JSX.Element {
  const { data: charges = [] } = useDossierCharges(dossierId);
  const { data: paiements = [] } = useDossierPaiements(dossierId);
  const { data: sim } = useDossierSimulateur(dossierId);
  const upsertCharge = useUpsertDossierCharge();
  const removeCharge = useDeleteDossierCharge();
  const addPaiement = useAddDossierPaiement();

  const [newCat, setNewCat] = useState<DossierChargeCategorie>('honoraires_formateur');
  const [newLib, setNewLib] = useState('');
  const [newMnt, setNewMnt] = useState('');
  const [newPaiementType, setNewPaiementType] = useState<DossierPaiementType>(
    'encaissement_client_acompte',
  );
  const [newPaiementMnt, setNewPaiementMnt] = useState('');

  const handleAddCharge = async (): Promise<void> => {
    if (!newLib.trim() || !newMnt) return;
    await upsertCharge.mutateAsync({
      dossier_id: dossierId,
      categorie: newCat,
      libelle: newLib.trim(),
      montant: Number(newMnt),
      ordre: charges.length + 1,
    });
    toast.success('Charge ajoutée');
    setNewLib('');
    setNewMnt('');
  };

  const handleAddPaiement = async (): Promise<void> => {
    if (!newPaiementMnt) return;
    await addPaiement.mutateAsync({
      dossier_id: dossierId,
      type: newPaiementType,
      montant: Number(newPaiementMnt),
      mode: 'virement',
      statut: 'paye',
      date_paiement: new Date().toISOString().slice(0, 10),
    });
    toast.success('Paiement ajouté');
    setNewPaiementMnt('');
  };

  return (
    <div className="space-y-5">
      <BenefitSimulatorCard
        prixVenteHT={prixVenteHT}
        totalCharges={Number(sim?.total_charges ?? 0)}
        charges={Object.entries(sim?.charges_par_categorie ?? {}).map(([k, v]) => ({
          categorie: k,
          total: Number(v),
        }))}
      />

      <DataCard title="Charges" subtitle={`${charges.length} ligne(s)`}>
        <table className="w-full text-xs">
          <thead className="text-left text-[10px] uppercase tracking-widest text-slate-400">
            <tr>
              <th className="py-2">Catégorie</th>
              <th className="py-2">Libellé</th>
              <th className="py-2 text-right">Montant</th>
              <th className="py-2"></th>
            </tr>
          </thead>
          <tbody>
            {charges.map((c) => (
              <tr key={c.id} className="border-t border-slate-100">
                <td className="py-2">
                  {CATEGORIES.find((cat) => cat.value === c.categorie)?.label ?? c.categorie}
                </td>
                <td className="py-2 text-slate-700">{c.libelle}</td>
                <td className="py-2 text-right">
                  <MoneyAmount amount={c.montant} variant="inline" />
                </td>
                <td className="py-2 text-right">
                  <button
                    onClick={() =>
                      void removeCharge.mutateAsync({ id: c.id, dossier_id: dossierId })
                    }
                    className="text-rose-500 hover:text-rose-700"
                    aria-label="Supprimer"
                  >
                    <Trash2 className="h-3.5 w-3.5" />
                  </button>
                </td>
              </tr>
            ))}
            {charges.length === 0 && (
              <tr>
                <td colSpan={4} className="py-3 text-center text-slate-400">
                  Aucune charge — ajoutez la première ci-dessous.
                </td>
              </tr>
            )}
            <tr className="border-t border-slate-200 bg-slate-50/40">
              <td className="py-2">
                <select
                  value={newCat}
                  onChange={(e) => setNewCat(e.target.value as DossierChargeCategorie)}
                  className="h-8 w-full rounded-lg border border-slate-200 bg-white px-2 text-xs"
                >
                  {CATEGORIES.map((c) => (
                    <option key={c.value} value={c.value}>
                      {c.label}
                    </option>
                  ))}
                </select>
              </td>
              <td className="py-2">
                <input
                  value={newLib}
                  onChange={(e) => setNewLib(e.target.value)}
                  placeholder="Libellé"
                  className="h-8 w-full rounded-lg border border-slate-200 bg-white px-2 text-xs"
                />
              </td>
              <td className="py-2 text-right">
                <input
                  type="number"
                  value={newMnt}
                  onChange={(e) => setNewMnt(e.target.value)}
                  placeholder="Montant"
                  className="h-8 w-32 rounded-lg border border-slate-200 bg-white px-2 text-right text-xs tabular-nums"
                />
              </td>
              <td className="py-2 text-right">
                <ActionButton
                  variant="gradient"
                  module="formation_b2b"
                  size="sm"
                  icon={<Plus className="h-3 w-3" />}
                  onClick={() => void handleAddCharge()}
                  loading={upsertCharge.isPending}
                >
                  Ajouter
                </ActionButton>
              </td>
            </tr>
          </tbody>
        </table>
      </DataCard>

      <DataCard title="Paiements" subtitle={`${paiements.length} mouvement(s)`}>
        <table className="w-full text-xs">
          <thead className="text-left text-[10px] uppercase tracking-widest text-slate-400">
            <tr>
              <th className="py-2">Date</th>
              <th className="py-2">Type</th>
              <th className="py-2">Mode</th>
              <th className="py-2">Statut</th>
              <th className="py-2 text-right">Montant</th>
            </tr>
          </thead>
          <tbody>
            {paiements.map((p) => (
              <tr key={p.id} className="border-t border-slate-100">
                <td className="py-2">
                  <DateBadge date={p.date_paiement} />
                </td>
                <td className="py-2 text-slate-700">{p.type.replace(/_/g, ' ')}</td>
                <td className="py-2 text-slate-700">{p.mode}</td>
                <td className="py-2">
                  <StatusBadge
                    status={
                      p.statut === 'paye'
                        ? 'paye'
                        : p.statut === 'en_retard'
                          ? 'en_retard'
                          : p.statut === 'planifie'
                            ? 'planifie'
                            : p.statut === 'a_payer'
                              ? 'a_payer'
                              : 'annule'
                    }
                  />
                </td>
                <td className="py-2 text-right">
                  <MoneyAmount amount={p.montant} variant="inline" />
                </td>
              </tr>
            ))}
            {paiements.length === 0 && (
              <tr>
                <td colSpan={5} className="py-3 text-center text-slate-400">
                  Aucun paiement enregistré.
                </td>
              </tr>
            )}
            <tr className="border-t border-slate-200 bg-slate-50/40">
              <td className="py-2 text-slate-400">Aujourd&apos;hui</td>
              <td className="py-2">
                <select
                  value={newPaiementType}
                  onChange={(e) => setNewPaiementType(e.target.value as DossierPaiementType)}
                  className="h-8 w-full rounded-lg border border-slate-200 bg-white px-2 text-xs"
                >
                  <option value="encaissement_client_acompte">Encaissement acompte</option>
                  <option value="encaissement_client_solde">Encaissement solde</option>
                  <option value="paiement_formateur">Paiement formateur</option>
                  <option value="paiement_lieu">Paiement lieu</option>
                  <option value="paiement_traiteur">Paiement traiteur</option>
                  <option value="paiement_autre_charge">Paiement autre</option>
                </select>
              </td>
              <td className="py-2 text-slate-400">virement</td>
              <td className="py-2 text-slate-400">payé</td>
              <td className="py-2 text-right">
                <div className="inline-flex items-center gap-2">
                  <input
                    type="number"
                    value={newPaiementMnt}
                    onChange={(e) => setNewPaiementMnt(e.target.value)}
                    placeholder="Montant"
                    className="h-8 w-28 rounded-lg border border-slate-200 bg-white px-2 text-right text-xs tabular-nums"
                  />
                  <ActionButton
                    variant="gradient"
                    module="echeances"
                    size="sm"
                    icon={<Plus className="h-3 w-3" />}
                    onClick={() => void handleAddPaiement()}
                    loading={addPaiement.isPending}
                  >
                    Ajouter
                  </ActionButton>
                </div>
              </td>
            </tr>
          </tbody>
        </table>
      </DataCard>
    </div>
  );
}

function Info({ label, value }: { label: string; value: React.ReactNode }): JSX.Element {
  return (
    <div>
      <div className="text-[10px] font-bold uppercase tracking-widest text-slate-400">{label}</div>
      <div className="mt-0.5">{value}</div>
    </div>
  );
}

function Row({ label, value }: { label: string; value: React.ReactNode }): JSX.Element {
  return (
    <div className="flex items-center justify-between gap-2">
      <span className="text-slate-600">{label}</span>
      {value}
    </div>
  );
}
